import { fileTypeFromBuffer } from "file-type";
import validator from "validator";
import Papa from "papaparse";

import { DetectedFile } from "./types";

export interface LinkTypeInfo {
  type: string;
  icon: string;
  label: string;
  error?: boolean;
}

// Common file type mappings
const FILE_TYPE_MAPPINGS = {
  image: { icon: "Image", label: "Image File" },
  video: { icon: "Play", label: "Video File" },
  audio: { icon: "Volume2", label: "Audio File" },
  pdf: { icon: "FileText", label: "PDF Document" },
  link: { icon: "ExternalLink", label: "Web Link" },
} as const;

const MIME_TYPE_MAPPINGS = {
  "application/pdf": { type: "pdf" as const, extension: "pdf" },
  "text/csv": { type: "csv" as const, extension: "csv" },
  "application/json": { type: "json" as const, extension: "json" },
  "text/plain": { type: "text" as const, extension: "txt" },
} as const;

const createDetectedFile = (
  type: DetectedFile["type"],
  mimeType?: string,
  extension?: string,
): DetectedFile => ({
  type,
  ...(mimeType && { mimeType }),
  ...(extension && { extension }),
});

const createLinkTypeInfo = (
  type: keyof typeof FILE_TYPE_MAPPINGS,
  customLabel?: string,
): LinkTypeInfo => ({
  type,
  icon: FILE_TYPE_MAPPINGS[type].icon,
  label: customLabel || FILE_TYPE_MAPPINGS[type].label,
  error: false,
});

export const detectFileTypeFromMediaObject = (mediaObject: {
  title?: string;
  type?: string;
  url: string;
  format?: string;
}): DetectedFile => {
  const { type: mimeType, format, url } = mediaObject;

  if (!mimeType || mimeType === "neither") {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return { type: "link", linkTypeInfo: detectFileTypeFromUrl(url) };
  }

  const mediaType = mimeType.split("/")[0] as "image" | "video" | "audio";
  const isPdf = mimeType.includes("pdf");

  if (isPdf) {
    return format === "base64"
      ? createDetectedFile("pdf", mimeType, "pdf")
      : {
          type: "link",
          linkTypeInfo: createLinkTypeInfo("pdf"),
        };
  }

  if (["image", "video", "audio"].includes(mediaType)) {
    return format === "base64"
      ? createDetectedFile(mediaType, mimeType, mediaType)
      : { type: "link", linkTypeInfo: createLinkTypeInfo(mediaType) };
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return { type: "link", linkTypeInfo: detectFileTypeFromUrl(url) };
};

export const isDataURL = (str: string): boolean => validator.isDataURI(str);

export const isURL = (str: string): boolean => validator.isURL(str, { require_protocol: true });

const isCloudStorageURL = (str: string): boolean =>
  /^https?:\/\/(?:[\w.-]+\.)?(?:s3|storage|firebasestorage|cloudfront|digitaloceanspaces|blob\.core\.windows\.net|backblazeb2|wasabisys|aliyuncs)\.[\w.-]+\/[\w/.\-%?=&]+$/.test(
    str,
  );

const COMMON_SIGNATURES = [
  "JVBERi0x", // PDF
  "iVBORw0KGgo", // PNG
  "/9j/", // JPEG/JPG
  "R0lGODdh", // GIF87a
  "R0lGODlh", // GIF89a
  "UklGR", // RIFF container (WebP, AVI, WAV)
  "UEsDB", // ZIP/DOCX/XLSX/PPTX
  "UmFyI", // RAR archive
  "N3q8", // 7-Zip
  "H4sI", // GZIP
  "Qk", // BMP
  "SUkq", // TIFF (little-endian)
  "TU0A", // TIFF (big-endian)
  "SUQz", // MP3 (ID3v2)
  "//u", // MP3 (MPEG audio)
  "//s", // MP3 (MPEG audio)
  "T2dn", // OGG
  "ZkxhQ", // FLAC
  "AAABA", // ICO (Windows icon)
  "PHN2Zy", // SVG (<svg)
  "PD94b", // XML/SVG (<?xml)
  "TVqQ", // EXE/DLL (Windows executable)
];

const hasSignature = (value: string): boolean =>
  COMMON_SIGNATURES.some(signature => value.includes(signature));

export const isSimpleValue = (value?: string): boolean => {
  if (!value) {
    return true;
  }

  const trimmed = value.trim();

  if (trimmed.length < 10) {
    return true;
  }

  if (hasSignature(trimmed)) {
    return false;
  }

  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return true;
  }

  if (/^(true|false)$/i.test(trimmed)) {
    return true;
  }

  if (/^[a-zA-Z\s]+$/.test(trimmed) && trimmed.length < 50) {
    return true;
  }

  if (isURL(trimmed) || isDataURL(trimmed) || isCloudStorageURL(trimmed)) {
    return false;
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      const hasFileContent = parsed.some(
        item =>
          isURL(item) ||
          isDataURL(item) ||
          isCloudStorageURL(item) ||
          (item.length > 100 && validator.isBase64(item)),
      );

      if (hasFileContent) {
        return false;
      }

      return true;
    }
  } catch {
    return true;
  }

  return true;
};

export const extractBase64 = (str: string): string => (str.includes(",") ? str.split(",")[1] : str);

const detectFileTypeFromMimeType = (mimeType: string): DetectedFile => {
  const extension = mimeType.split("/")[1];

  if (mimeType in MIME_TYPE_MAPPINGS) {
    const mapping = MIME_TYPE_MAPPINGS[mimeType as keyof typeof MIME_TYPE_MAPPINGS];
    return createDetectedFile(mapping.type, mimeType, mapping.extension);
  }

  if (mimeType.includes("csv")) {
    return createDetectedFile("csv", mimeType, extension);
  }
  if (mimeType.includes("json")) {
    return createDetectedFile("json", mimeType, extension);
  }
  if (mimeType.includes("txt")) {
    return createDetectedFile("text", mimeType, extension);
  }
  if (mimeType.startsWith("image/")) {
    return createDetectedFile("image", mimeType, extension);
  }
  if (mimeType.startsWith("video/")) {
    return createDetectedFile("video", mimeType, extension);
  }
  if (mimeType.startsWith("audio/")) {
    return createDetectedFile("audio", mimeType, extension);
  }

  return createDetectedFile("unknown", mimeType, extension);
};

export const detectFileTypeFromFormat = (format: string): DetectedFile["type"] => {
  switch (format) {
    case "image":
      return "image";
    case "video":
      return "video";
    case "audio":
      return "audio";
    case "pdf":
      return "pdf";
    case "text":
      return "text";
    case "url":
    case "neither":
      return "link";
    default:
      return "unknown";
  }
};

export const base64ToBuffer = (base64: string): Uint8Array => {
  try {
    const cleanBase64 = extractBase64(base64);
    let padded = cleanBase64.replace(/[^A-Za-z0-9+/]/g, "");
    while (padded.length % 4 !== 0) {
      padded += "=";
    }

    const binaryString = atob(padded);
    return new Uint8Array(binaryString.length).map((_, i) => binaryString.charCodeAt(i));
  } catch {
    throw new Error("Invalid base64 string");
  }
};

export const isTextContent = (buffer: Uint8Array): boolean => {
  if (buffer.length === 0) {
    return false;
  }

  if (buffer.length >= 3 && buffer[0] === 0xef && buffer[1] === 0xbb && buffer[2] === 0xbf) {
    return true;
  }

  const sampleSize = Math.min(512, buffer.length);
  let printableCount = 0;
  let controlCount = 0;

  for (let i = 0; i < sampleSize; i++) {
    const byte = buffer[i];

    if (byte === 0) {
      return false;
    }

    if ((byte >= 32 && byte <= 126) || byte === 9 || byte === 10 || byte === 13) {
      // eslint-disable-next-line no-plusplus
      printableCount++;
    } else if (byte < 32 || byte === 127) {
      // eslint-disable-next-line no-plusplus
      controlCount++;
    } else if (byte >= 128 && byte <= 191) {
      // eslint-disable-next-line no-plusplus
      printableCount++;
    } else if (byte >= 192 && byte <= 247) {
      // eslint-disable-next-line no-plusplus
      printableCount++;
    }
  }

  const printableRatio = printableCount / sampleSize;
  const controlRatio = controlCount / sampleSize;

  return printableRatio >= 0.95 && controlRatio <= 0.05;
};

export const isMediaURL = (str: string): boolean => {
  if (!isURL(str)) {
    return false;
  }

  return (
    /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif|mp4|webm|mov|avi|mkv|m4v|3gp|ogv|wmv|flv|mp3|wav|ogg|aac|flac|m4a|wma|opus|pdf|doc|docx|xls|xlsx|ppt|pptx|csv)(\?|#|$)/i.test(
      str,
    ) || isCloudStorageURL(str)
  );
};

export const isCSVContent = (text: string): boolean => {
  if (!text || text.trim().length === 0) {
    return false;
  }

  if (validator.isJSON(text.trim())) {
    return false;
  }

  try {
    const result = Papa.parse(text, {
      delimiter: "",
      skipEmptyLines: true,
      preview: 10,
    });

    if (!result.data || result.data.length < 2) {
      return false;
    }

    const firstRow = result.data[0] as string[];
    if (!firstRow || firstRow.length < 2) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
};

const URL_PATTERNS = {
  image: /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico|tiff|tif)($|\?|#)/i,
  video: /\.(mp4|avi|mov|wmv|flv|webm|mkv|m4v|3gp|ogv)($|\?|#)/i,
  audio: /\.(mp3|wav|ogg|aac|flac|m4a|wma|opus)($|\?|#)/i,
  pdf: /\.(pdf|doc|docx|txt|rtf|xls|xlsx|ppt|pptx)($|\?|#)/i,
};

export const detectFileTypeFromUrl = (url: string): LinkTypeInfo => {
  if (url.includes("cdn")) {
    return createLinkTypeInfo("image");
  }

  for (const [type, pattern] of Object.entries(URL_PATTERNS)) {
    if (pattern.test(url)) {
      return createLinkTypeInfo(type as keyof typeof FILE_TYPE_MAPPINGS);
    }
  }

  return createLinkTypeInfo("link", "Web Link");
};

const detectFromURL = async (url: string): Promise<DetectedFile> => {
  if (isCloudStorageURL(url)) {
    return {
      type: "link",
      linkTypeInfo: createLinkTypeInfo("image", "Cloud Storage File"),
    };
  }

  return {
    type: "link",
    linkTypeInfo: detectFileTypeFromUrl(url.toLowerCase()),
  };
};

export const detectFileType = async (input: string): Promise<DetectedFile | null> => {
  if (!input || typeof input !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(input);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "title" in parsed &&
      "type" in parsed &&
      "url" in parsed
    ) {
      return detectFileTypeFromMediaObject(parsed);
    }
  } catch {
    // Do nothing
  }

  if (isDataURL(input)) {
    const mimeType = input.split(";")[0].split(":")[1];

    if (mimeType) {
      return detectFileTypeFromMimeType(mimeType);
    }

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return await detectFileTypeFromBuffer(base64ToBuffer(input));
  }

  if (isURL(input)) {
    return detectFromURL(input);
  }

  if (validator.isBase64(input)) {
    // eslint-disable-next-line no-console
    console.log("isBase64");
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    return await detectFileTypeFromBuffer(base64ToBuffer(input));
  }

  return null;
};

const detectFileTypeFromBuffer = async (buffer: Uint8Array): Promise<DetectedFile | null> => {
  const result = await fileTypeFromBuffer(buffer);

  if (result) {
    const { ext, mime } = result;

    if (mime === "application/octet-stream" || ext === "bin") {
      return null;
    }

    const detectedFile = detectFileTypeFromMimeType(mime);

    if (detectedFile.type === "unknown") {
      return createDetectedFile("unknown", "text/plain", "unknown");
    }

    return detectedFile.mimeType === mime
      ? { ...detectedFile, extension: ext }
      : { ...detectedFile, extension: ext };
  }

  if (isTextContent(buffer)) {
    try {
      const textContent = new TextDecoder("utf-8", { fatal: true }).decode(buffer);

      if (isCSVContent(textContent)) {
        return createDetectedFile("csv", "text/csv", "csv");
      }
      if (validator.isJSON(textContent)) {
        return createDetectedFile("json", "application/json", "json");
      }
      return createDetectedFile("text", "text/plain", "txt");
    } catch {
      return null;
    }
  }

  return null;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return "0 B";
  }
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
};

export const getFileSizeFromBase64 = (base64: string): string => {
  const cleanBase64 = extractBase64(base64).replace(/[^A-Za-z0-9+/]/g, "");
  const padding = (cleanBase64.match(/=/g) || []).length;
  const sizeInBytes = (cleanBase64.length * 3) / 4 - padding;
  return formatFileSize(Math.floor(sizeInBytes));
};

export const cleanBase64Data = (data: string): string => {
  let cleaned = data;

  if (cleaned.startsWith("[") && cleaned.endsWith("]")) {
    try {
      const parsed = JSON.parse(cleaned);
      if (Array.isArray(parsed) && parsed.length > 0) {
        cleaned = parsed[0];
      }
    } catch {
      cleaned = cleaned.slice(1, -1).replace(/"/g, "");
    }
  }

  return cleaned.replace(/^"(.*)"$/, "$1");
};

export const createDataUrl = (base64: string, mimeType: string): string => {
  const cleaned = cleanBase64Data(base64);
  return cleaned.startsWith("data:") ? cleaned : `data:${mimeType};base64,${cleaned}`;
};

export interface DetectedFileItem {
  data: string;
  fileType: DetectedFile | null;
  isEditable: boolean;
}

export const processInputValue = async (
  value: string,
): Promise<{
  items: DetectedFileItem[] | undefined;
  isFileCollection: boolean;
  fileCount?: number;
}> => {
  let valuesToProcess: string[] = [];

  try {
    const parsed = JSON.parse(value);
    valuesToProcess = Array.isArray(parsed)
      ? parsed.map(item => (typeof item === "string" ? item : JSON.stringify(item)))
      : [typeof parsed === "string" ? parsed : JSON.stringify(parsed)];
  } catch {
    valuesToProcess = [value];
  }

  const items = await Promise.all(
    valuesToProcess.map(async item => {
      const cleaned = typeof item === "string" ? item.replace(/"/g, "") : item;
      const fileType = await detectFileType(cleaned.slice(0, 10000));

      return {
        data: cleaned,
        fileType,
        isEditable: fileType?.type === "link" || isMediaURL(cleaned),
      };
    }),
  );

  const fileItems = items.filter(item => item.fileType !== null);

  return {
    items: fileItems,
    isFileCollection: fileItems.length > 0,
    fileCount: fileItems.length,
  };
};

export const processInputValueWithMetadata = async (
  value: string,
): Promise<{
  items: DetectedFileItem[];
  isFileCollection: boolean;
  fileCount: number;
  detectedFileValues: string[];
  hasValidFiles: boolean;
}> => {
  const result = await processInputValue(value);

  return {
    items: result.items || [],
    isFileCollection: result.isFileCollection,
    fileCount: result.fileCount || 0,
    detectedFileValues: (result.items || []).map(item => item.data),
    hasValidFiles: (result.items || []).length > 0,
  };
};
